import { earlierLabel, threadShown } from "../../server/rules/folds";
import type { Compose, Entry } from "../../server/core/conversations";
import { el } from "./dom";
import { icon } from "./icons";
import { emailChips, type EmailChips } from "./recipients";
import { chip } from "./record";

// A conversation (brief 1B-5, mockup v20 inboxRec): the thread, where every message says who on the
// team sent it, from which line and what happened to it; yellow notes with their @mentions; calls
// with their length; and the composer with Text, Email, Call and Note, From and To. Used by an
// inbox item and by the Messages card on every client and person record.

export type Mode = "text" | "email" | "call" | "note";

const MODE_LABEL: Record<Mode, string> = {
  text: "Text",
  email: "Email",
  call: "Call",
  note: "Note",
};
const MODE_ICON: Record<Mode, string> = {
  text: "comment",
  email: "mail",
  call: "device-mobile",
  note: "note",
};

/** A note's words with each @mention in bold. */
function noteBody(body: string, mentions: readonly string[]): (Node | string)[] {
  if (!mentions.length) return [body];
  const names = [...mentions].sort((a, b) => b.length - a.length);
  const out: (Node | string)[] = [];
  let rest = body;
  for (;;) {
    let at = -1;
    let hit = "";
    for (const n of names) {
      for (const form of [n, n.split(" ")[0] ?? n]) {
        const i = rest.toLowerCase().indexOf(`@${form.toLowerCase()}`);
        if (i >= 0 && (at < 0 || i < at)) {
          at = i;
          hit = rest.slice(i, i + form.length + 1);
        }
      }
    }
    if (at < 0) break;
    out.push(rest.slice(0, at), el("b", {}, hit));
    rest = rest.slice(at + hit.length);
  }
  out.push(rest);
  return out;
}

/** The thread (mockup .thread): oldest first. */
/** Whether a thread shows every message: kept across live redraws of the same thread. */
export interface ThreadFold {
  all: boolean;
}

/**
 * The thread (brief 1E-32): newest first, the newest message, then "Show n earlier" folding the
 * rest; one click shows them all. Entries come oldest first.
 */
export function threadView(
  entries: readonly Entry[],
  onFile?: (f: { id: string; name: string }) => Node,
  fold: ThreadFold = { all: false },
  /** Brief 1E-34: a record's Messages draw each entry their own way (who, channel, visit, Forward). */
  render?: (m: Entry) => HTMLElement,
  empty = "No messages yet.",
): HTMLElement {
  if (!entries.length) return el("div", { class: "thread" }, el("p", { class: "muted" }, empty));
  const { shown, earlier } = threadShown(entries, fold.all);
  const wrap = el("div", { class: "thread", role: "list", "aria-label": "Conversation" });
  const more = earlier
    ? el("button", { type: "button", class: "btn sm ghost earlier" }, earlierLabel(earlier))
    : null;
  more?.addEventListener("click", () => {
    fold.all = true;
    const all = threadView(entries, onFile, fold, render, empty);
    wrap.replaceWith(all);
    all.querySelectorAll<HTMLElement>(".msg")[1]?.setAttribute("tabindex", "-1");
    all.querySelectorAll<HTMLElement>(".msg")[1]?.focus();
  });
  wrap.append(
    ...shown.map((m: Entry) => (render ? render(m) : entryView(m, onFile))),
    ...(more ? [more] : []),
  );
  return wrap;
}

/** One message, note or call in a thread. */
export function entryView(
  m: Entry,
  onFile?: (f: { id: string; name: string }) => Node,
): HTMLElement {
  {
    if (m.kind === "note")
      return el(
        "div",
        { class: "msg note-m", role: "listitem", "data-entry": m.id },
        el(
          "span",
          { class: "meta" },
          icon("note"),
          ` Note · ${m.who} · ${m.when} · only the team sees this`,
        ),
        el("span", { class: "body" }, ...noteBody(m.body, m.mentions)),
      );
    if (m.kind === "call")
      return el(
        "div",
        { class: "msg call-m", role: "listitem", "data-entry": m.id },
        el(
          "span",
          { class: "meta" },
          icon("device-mobile"),
          ` Call · ${m.who} · ${m.line} · ${m.when}`,
        ),
        el(
          "span",
          { class: "body" },
          m.body,
          " ",
          m.status ? chip(m.status.label, m.status.level) : "",
        ),
        m.voicemail ? voicemailView(m.voicemail, onFile) : null,
      );
    const out = m.kind !== "in";
    return el(
      "div",
      { class: `msg${out ? " out" : ""}`, role: "listitem", "data-entry": m.id },
      el(
        "span",
        { class: "meta" },
        [
          out ? m.who : null,
          m.channelLabel,
          m.kind === "link" ? `to ${m.to}` : out ? `from ${m.line}` : m.line,
          m.when,
        ]
          .filter(Boolean)
          .join(" · "),
      ),
      el("span", { class: "body" }, m.body),
      m.attachments.length && onFile
        ? el("span", { class: "linkrow", style: "margin:4px 0 0" }, ...m.attachments.map(onFile))
        : null,
      m.status ? el("span", { class: "meta" }, chip(m.status.label, m.status.level)) : null,
    );
  }
}

/**
 * A missed call's voicemail (brief 1B-6): played here, from FlowCare's private files, by people
 * who can view the record; its transcript under it ("Transcript coming" until Twilio sends it).
 */
function voicemailView(
  v: NonNullable<Entry["voicemail"]>,
  onFile?: (f: { id: string; name: string }) => Node,
): HTMLElement {
  const label = `Voicemail ${v.length}`;
  return el(
    "span",
    { class: "vm" },
    el("b", {}, label),
    v.file
      ? el("audio", {
          controls: true,
          preload: "none",
          // Played here, never downloaded (brief 1B-9).
          src: `/api/files/${encodeURIComponent(v.file.id)}/play`,
          "aria-label": label,
        })
      : el("span", { class: "small muted" }, " Copying the recording…"),
    el("span", { class: v.transcript ? "body" : "small muted" }, v.text),
    v.file && onFile ? el("span", { class: "linkrow" }, onFile(v.file)) : null,
  );
}

export interface ComposerOptions {
  compose: Compose;
  mode: Mode;
  /** Where To starts (the conversation's other side). */
  to?: string | null;
  /** The inbox shows Send (keeps it open) and Send and done; a record shows Send. */
  inbox: boolean;
  /** "Last from us: Tasha Green · 10/23/2026 3:12 PM". */
  last?: string | null;
  send: (b: {
    mode: Mode;
    from: string | null;
    to: string | null;
    body: string;
    keepOpen: boolean;
  }) => Promise<string | null>;
  /** Called when the mode changes, so a redraw keeps it. */
  onMode?: (m: Mode) => void;
  /** The inbox item a reply answers: AI's Draft a reply drafts from its conversation (1B-8). */
  aiItem?: string;
}

export interface Composer {
  el: HTMLElement;
  /** R reply, N note: switch and put the cursor in the box. */
  focus: (mode?: Mode) => void;
}

/** The composer: Text, Email, Call or Note, From and To (mockup v20). */
export function composer(o: ComposerOptions): Composer {
  let mode = o.mode;
  const wrap = el("div", { class: "composer2" });
  let box: HTMLTextAreaElement | null = null;

  function draw() {
    const tabs = el(
      "div",
      { class: "tabs", role: "tablist", "aria-label": "Reply as" },
      ...(["text", "email", "call", "note"] as const).map((m) => {
        const b = el(
          "button",
          { type: "button", role: "tab", "aria-selected": String(mode === m) },
          icon(MODE_ICON[m]),
          ` ${MODE_LABEL[m]}`,
        );
        b.addEventListener("click", () => {
          mode = m;
          o.onMode?.(m);
          draw();
        });
        return b;
      }),
    );
    const said = el("div", { class: "note", role: "alert", hidden: true });
    const fields: HTMLElement[] = [];
    let from: HTMLSelectElement | null = null;
    let to: HTMLInputElement | null = null;
    // Email goes to several people at once (brief 1E-15): chips instead of one box.
    let toMany: EmailChips | null = null;
    box = null;
    if (mode === "note") {
      box = el("textarea", { id: "cvb", placeholder: "@Name please call them back today." });
      fields.push(
        el(
          "div",
          { class: "field" },
          el("label", { for: "cvb" }, "Note for the team (type @ to mention someone)"),
          box,
          mentionPicker(box, o.compose.people),
        ),
      );
    } else {
      const lines = o.compose.from[mode];
      from = el(
        "select",
        { id: "cvf" },
        ...(lines.length
          ? lines.map((l) => el("option", { value: l.id }, l.name))
          : [
              el(
                "option",
                { value: "" },
                mode === "email" ? "No mailbox connected" : "No number connected",
              ),
            ]),
      );
      const options = o.compose.to[mode];
      const listId = `cvt-${mode}`;
      const start =
        (o.to && (mode === "email" ? o.to.includes("@") : !o.to.includes("@")) ? o.to : null) ??
        options[0]?.value ??
        "";
      if (mode === "email")
        toMany = emailChips({
          id: "cvt",
          label: "To (email)",
          start: start ? [start] : [],
          options,
        });
      else
        to = el("input", {
          id: "cvt",
          type: "text",
          value: start,
          list: listId,
          autocomplete: "off",
        });
      fields.push(
        el(
          "div",
          { class: "grid2", style: "gap:10px" },
          el("div", { class: "field" }, el("label", { for: "cvf" }, "From"), from),
          toMany
            ? toMany.el
            : el(
                "div",
                { class: "field" },
                el("label", { for: "cvt" }, "To"),
                to,
                el(
                  "datalist",
                  { id: listId },
                  ...options.map((t) => el("option", { value: t.value }, t.label)),
                ),
              ),
        ),
      );
      if (mode === "call")
        fields.push(
          el(
            "p",
            { class: "small muted", style: "margin:0" },
            "The caller's phone rings first, then the call connects. The call shows here with its length.",
          ),
        );
      else {
        box = el("textarea", { id: "cvb", placeholder: "Write a reply", "data-ai-item": o.aiItem });
        fields.push(
          el(
            "div",
            { class: "field" },
            el(
              "label",
              { for: "cvb" },
              mode === "text"
                ? "Text (no documents or patient details; send a secure link instead)"
                : "Email",
            ),
            box,
          ),
        );
      }
    }
    const buttons: HTMLButtonElement[] = [];
    const go = (label: (Node | string)[], cls: string, keepOpen: boolean) => {
      const b = el("button", { class: cls, type: "button" }, ...label);
      b.addEventListener("click", async () => {
        for (const x of buttons) x.disabled = true;
        const error = await o.send({
          mode,
          from: from?.value || null,
          to: toMany ? toMany.values().join(", ") || null : to?.value.trim() || null,
          body: box?.value ?? "",
          keepOpen,
        });
        for (const x of buttons) x.disabled = false;
        if (error) {
          said.hidden = false;
          said.textContent = error;
        }
      });
      buttons.push(b);
      return b;
    };
    if (mode === "note") go([icon("note"), " Add note"], "btn pri", true);
    else if (mode === "call") go([icon("device-mobile"), " Call"], "btn pri", true);
    else if (o.inbox) {
      go([icon("paper-airplane"), " Send"], "btn", true);
      go([icon("check"), " Send and done"], "btn pri", false);
    } else go([icon("paper-airplane"), " Send"], "btn pri", true);
    wrap.replaceChildren(
      tabs,
      el(
        "div",
        { class: "form", style: "max-width:none;margin-top:10px" },
        said,
        ...fields,
        el(
          "div",
          { class: "actions", style: "justify-content:space-between" },
          el("span", { class: "small muted" }, o.last ?? ""),
          el("span", { class: "actions" }, ...buttons),
        ),
      ),
    );
  }
  draw();
  return {
    el: wrap,
    focus(m) {
      if (m && m !== mode) {
        mode = m;
        o.onMode?.(m);
        draw();
      }
      (box ?? wrap.querySelector<HTMLElement>("select,input"))?.focus();
    },
  };
}

/** Typing @ lists the people who work the inbox; picking one puts "@Full Name " in the note. */
function mentionPicker(box: HTMLTextAreaElement, people: readonly { id: string; name: string }[]) {
  const list = el("div", {
    class: "picks mentions",
    role: "listbox",
    "aria-label": "Mention someone",
    hidden: true,
  });
  const update = () => {
    const before = box.value.slice(0, box.selectionStart ?? box.value.length);
    const m = /(^|\s)@([\w.'-]*)$/.exec(before);
    if (!m) {
      list.hidden = true;
      return;
    }
    const q = (m[2] ?? "").toLowerCase();
    const found = people.filter(
      (p) =>
        p.name
          .toLowerCase()
          .split(/\s+/)
          .some((w) => w.startsWith(q)) || p.name.toLowerCase().startsWith(q),
    );
    list.hidden = !found.length;
    list.replaceChildren(
      ...found.slice(0, 6).map((p) => {
        const b = el(
          "button",
          { type: "button", class: "btn sm", role: "option" },
          icon("person"),
          ` ${p.name}`,
        );
        b.addEventListener("mousedown", (e) => e.preventDefault());
        b.addEventListener("click", () => {
          const start = before.length - (m[2] ?? "").length - 1;
          const after = box.value.slice(before.length);
          box.value = `${box.value.slice(0, start)}@${p.name} ${after}`;
          const caret = start + p.name.length + 2;
          box.setSelectionRange(caret, caret);
          list.hidden = true;
          box.focus();
        });
        return b;
      }),
    );
  };
  box.addEventListener("input", update);
  box.addEventListener("keyup", (e) => {
    if (e.key === "Escape") list.hidden = true;
  });
  return list;
}

/** "Last from us: who · when" from the thread. */
export function lastFromUs(entries: readonly Entry[]): string | null {
  const last = [...entries]
    .reverse()
    .find((e) => e.kind === "out" || (e.kind === "call" && !e.incoming));
  return last ? `Last from the office: ${last.who} · ${last.when}` : null;
}

/** Whether a key press is someone typing (the clearing keys are off then). */
export function typing(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  return (
    e.ctrlKey ||
    e.metaKey ||
    e.altKey ||
    !!t?.closest?.("input,textarea,select,[contenteditable],[role=dialog]")
  );
}
